import { useState } from "react";
import { ApiError } from "@common/api";
import { formatSize } from "@common/format";
import { downloadCloudRom, type CloudRom } from "../../library/cloudLibrary";
import { errorText } from "@jkast90/kast-common/api-client";
import { useCloudRoms } from "../../library/queries";
import { isNative } from "../../desktop/desktop";
import { displayName } from "../saves";
import type { Emulator } from "../useEmulator";
import { Button, Dialog, Notice } from "@jkast90/kast-common/ui";

// Picks a game from the cloud library into the running player. Only
// offered signed in; the API client refuses without a token anyway.
export function CloudLibrary({ emu, onClose }: { emu: Emulator; onClose: () => void }) {
  const library = useCloudRoms();
  const roms = library.data ?? null;
  const [downloadError, setError] = useState<string | null>(null);
  const error = downloadError ?? (library.error ? errorText(library.error, "Could not load the cloud library.") : null);
  const [downloading, setDownloading] = useState<{ id: string; fraction: number } | null>(null);

  const download = async (rom: CloudRom) => {
    if (downloading) return;
    setDownloading({ id: rom.id, fraction: 0 });
    setError(null);
    try {
      const file = await downloadCloudRom(rom, (fraction) => setDownloading({ id: rom.id, fraction }));
      await emu.addRom(file);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Dialog open onClose={onClose} title="Cloud Library" footer={<Button onClick={onClose}>Close</Button>}>
      {roms === null && !error && <p className="kui-muted">Loading ROMs from cloud…</p>}
      {error && (
        <Notice tone="error" style={{ marginBottom: "1rem" }}>
          {error}
        </Notice>
      )}
      {roms?.length === 0 && (
        <p className="kui-muted">No ROMs in the cloud library yet. Add a game while signed in to share it.</p>
      )}
      {roms && roms.length > 0 && (
        <ul className="kui-list-reset rom-list">
          {roms.map((rom) => (
            <li key={rom.id}>
              <div>
                <span className="name" title={rom.name}>
                  {displayName(rom.name)}
                </span>
                <span className="kui-muted kui-small">
                  {rom.system.toUpperCase()} · {formatSize(rom.size)}
                </span>
              </div>
              {/* Disc games are listed but play in the desktop app, not this player. */}
              {isNative(rom.system) ? (
                <span className="kui-muted kui-small">Desktop app</span>
              ) : (
                <Button disabled={downloading !== null} onClick={() => download(rom)}>
                  {downloading?.id === rom.id ? `${Math.round(downloading.fraction * 100)}%` : "Download"}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
